import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, Trash2 } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

interface ProjectTimesheetsProps {
    projectId: string;
}

const ProjectTimesheets: React.FC<ProjectTimesheetsProps> = ({ projectId }) => {
    const [timesheets, setTimesheets] = useState<any[]>([]);
    const [tasks, setTasks] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);

    const [newEntry, setNewEntry] = useState({
        task_id: '', work_date: new Date().toISOString().split('T')[0], hours: '', description: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [timesRes, tasksRes] = await Promise.all([
                apiFetch(`/project-delivery/timesheets?project_id=${projectId}`),
                apiFetch(`/project-delivery/tasks?project_id=${projectId}`)
            ]);

            if (timesRes.ok) setTimesheets(await timesRes.json());
            if (tasksRes.ok) setTasks(await tasksRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => {
        fetchData();
    }, [projectId, fetchData]);

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/project-delivery/timesheets', {
                method: 'POST',
                body: JSON.stringify(newEntry)
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewEntry({ task_id: '', work_date: new Date().toISOString().split('T')[0], hours: '', description: '' });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const handleDeleteTimesheet = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this time entry?')) return;
        try {
            const res = await apiFetch(`/project-delivery/timesheets/${id}`, { method: 'DELETE' });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-500" /></div>;

    const totalHours = timesheets.reduce((sum, curr) => sum + parseFloat(curr.hours), 0);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <h3 className="text-xl font-bold text-gray-800">Timesheets</h3>
                    <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-bold">Total: {totalHours}h</span>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
                >
                    <Plus size={16} /> Log Time
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/50">
                            <tr>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Date</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Task</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Description</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Hours</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {timesheets.length === 0 ? (
                                <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">No time logged for this project yet.</td></tr>
                            ) : (
                                timesheets.map((t, i) => (
                                    <tr key={i} className="hover:bg-gray-50 text-sm">
                                        <td className="px-6 py-4 font-medium">{new Date(t.work_date).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 text-blue-600 font-semibold">{tasks.find(tk => tk.id === t.task_id)?.name || 'Direct Entry'}</td>
                                        <td className="px-6 py-4 text-gray-500">{t.description}</td>
                                        <td className="px-6 py-4 font-bold">{t.hours}h</td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => handleDeleteTimesheet(t.id)}
                                                className="p-1 text-gray-400 hover:text-red-500 rounded transition"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Log Work Time</h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Task</label>
                                <select className="w-full border p-2 rounded-lg" value={newEntry.task_id} onChange={e => setNewEntry({ ...newEntry, task_id: e.target.value })} required>
                                    <option value="">Select Task</option>
                                    {tasks.map(tk => <option key={tk.id} value={tk.id}>{tk.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Date</label>
                                    <input type="date" className="w-full border p-2 rounded-lg" value={newEntry.work_date} onChange={e => setNewEntry({ ...newEntry, work_date: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Hours</label>
                                    <input type="number" step="0.5" className="w-full border p-2 rounded-lg" value={newEntry.hours} onChange={e => setNewEntry({ ...newEntry, hours: e.target.value })} required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Comments</label>
                                <textarea className="w-full border p-2 rounded-lg" rows={3} value={newEntry.description} onChange={e => setNewEntry({ ...newEntry, description: e.target.value })} />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border rounded-lg font-bold">Cancel</button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold">Submit</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectTimesheets;
